import { landingPageAssets } from "./assets";

const contactEmail = "hi@anywhere.garden";
const headingPattern = /^(\d+(?:\.\d+)*)(?:\.)?\s+(.+)$/;

type DocumentBlock =
  | { id: string; level: 2 | 3; number: string; text: string; type: "heading" }
  | { id: string; text: string; type: "paragraph" }
  | { id: string; items: Array<string>; type: "list" };

function createDocumentBlocks(lines: Array<string>) {
  const blocks: Array<DocumentBlock> = [];
  let blockNumber = 0;

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const headingMatch = headingPattern.exec(line);

    if (headingMatch) {
      const number = headingMatch[1];
      blocks.push({
        id: `section-${number.replaceAll(".", "-")}`,
        level: number.includes(".") ? 3 : 2,
        number,
        text: headingMatch[2],
        type: "heading",
      });
      continue;
    }

    const nextLine = lines[index + 1];
    const startsList = line.endsWith(":") && nextLine?.endsWith(";");

    blocks.push({ id: `document-block-${blockNumber}`, text: line, type: "paragraph" });
    blockNumber += 1;

    if (!startsList) {
      continue;
    }

    const items: Array<string> = [];

    while (index + 1 < lines.length) {
      const item = lines[index + 1];

      if (headingPattern.test(item)) {
        break;
      }

      index += 1;
      items.push(
        item
          .replace(/^and\s+/i, "")
          .replace(/;\s+and$/i, "")
          .replace(/[.;]$/, ""),
      );

      if (item.endsWith(".")) {
        break;
      }
    }

    blocks.push({ id: `document-block-${blockNumber}`, items, type: "list" });
    blockNumber += 1;
  }

  return blocks;
}

function DocumentText({ text }: { text: string }) {
  if (!text.includes(contactEmail)) {
    return text;
  }

  const [before, after] = text.split(contactEmail);

  return (
    <>
      {before}
      <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
      {after}
    </>
  );
}

export function LegalDocumentPage({ source }: { source: string }) {
  const lines = source
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const [documentTitle, effectiveDate, lastUpdated, ...documentLines] = lines;
  const firstHeadingIndex = documentLines.findIndex((line) => headingPattern.test(line));
  const introductionLines = documentLines.slice(0, firstHeadingIndex);
  const documentBlocks = createDocumentBlocks(documentLines.slice(firstHeadingIndex));

  return (
    <main className="privacy-page">
      <div className="privacy-layout">
        <article className="privacy-document">
          <div className="privacy-introduction">
            <a aria-label="Back to Sky Garden" className="privacy-brand" href="/">
              <img alt="Sky Garden" src={landingPageAssets.paper.logo} />
            </a>
            <a
              aria-label="Visit Anywhere Studio"
              className="privacy-eyebrow"
              href="https://anywhere.garden/"
            >
              Anywhere Studio
            </a>
            <h1>{documentTitle}</h1>
            <div className="privacy-meta">
              <span>{effectiveDate}</span>
              <span>{lastUpdated}</span>
            </div>
            {introductionLines.map((line, index) => {
              const isContact = line.startsWith("Contact:");
              const className = isContact
                ? "privacy-contact"
                : index === 0
                  ? "privacy-lead"
                  : "privacy-intro-copy";

              return (
                <p className={className} key={line}>
                  <DocumentText text={line} />
                </p>
              );
            })}
          </div>
          <div className="privacy-content">
            {documentBlocks.map((block) => {
              if (block.type === "heading") {
                const headingText = `${block.number}. ${block.text}`;

                return block.level === 2 ? (
                  <h2 id={block.id} key={block.id}>
                    {headingText}
                  </h2>
                ) : (
                  <h3 id={block.id} key={block.id}>
                    {headingText}
                  </h3>
                );
              }

              if (block.type === "list") {
                return (
                  <ul key={block.id}>
                    {block.items.map((item) => (
                      <li key={`${block.id}-${item}`}>
                        <DocumentText text={item} />
                      </li>
                    ))}
                  </ul>
                );
              }

              return (
                <p key={block.id}>
                  <DocumentText text={block.text} />
                </p>
              );
            })}
          </div>
        </article>
      </div>
    </main>
  );
}
